import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Row,
    Col,
    Card,
    Typography,
    Select,
    Button,
    List,
    Avatar,
    Space,
    Input,
    message,
    Spin,
    Empty,
    Tag,
    Tooltip,
    Alert,
    Modal,
    Radio,
} from 'antd';
import ReactMarkdown from 'react-markdown';
import {
    RobotOutlined,
    SendOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    BookOutlined,
    BranchesOutlined,
    AimOutlined,
    BarChartOutlined,
    BankOutlined,
    UserOutlined,
    ArrowLeftOutlined,
    ExclamationCircleOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useStreamingChat } from '../../hooks/useStreamingChat';
import {
    useGetAIExpertsQuery,
    useCreateAIConversationMutation,
    useGetUserConversationsQuery,
    useGetAIConversationQuery,
    useDeleteAIConversationMutation,
    useGetAdminBrandsQuery,
    IAIExpert,
    IAIConversation,
} from '../../services/nestjs-base-api';

const { Title, Text } = Typography;
const { TextArea } = Input;

// CSS for typing animation
const typingAnimationStyle = `
  @keyframes typing {
    0%, 60%, 100% {
      transform: scale(1);
      opacity: 0.7;
    }
    30% {
      transform: scale(1.2);
      opacity: 1;
    }
  }
`;

const iconMap: { [key: string]: React.ReactNode } = {
    BookOpen: <BookOutlined />,
    Brain: <BranchesOutlined />,
    Target: <AimOutlined />,
    ChartBar: <BarChartOutlined />,
    Briefcase: <BankOutlined />,
};

const colorMap: { [key: string]: string } = {
    'bg-blue-500': '#3b82f6',
    'bg-purple-500': '#8b5cf6',
    'bg-green-500': '#10b981',
    'bg-orange-500': '#f97316',
    'bg-red-500': '#ef4444',
};

export const AIExpertsChatPage: React.FC = () => {
    const { user } = useAuth();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [selectedExpert, setSelectedExpert] = useState<IAIExpert | null>(null);
    const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
    const [currentConversation, setCurrentConversation] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [localMessages, setLocalMessages] = useState<any[]>([]);
    const [responseStyle, setResponseStyle] = useState<'brief' | 'detailed'>('brief');

    // Load saved responseStyle from localStorage when expert changes
    useEffect(() => {
        if (selectedExpert) {
            const saved = localStorage.getItem(`ai-expert-responseStyle-${selectedExpert.expertType}`);
            if (saved === 'brief' || saved === 'detailed') {
                setResponseStyle(saved);
            }
        }
    }, [selectedExpert]);

    const handleResponseStyleChange = (value: 'brief' | 'detailed') => {
        setResponseStyle(value);
        if (selectedExpert) {
            localStorage.setItem(`ai-expert-responseStyle-${selectedExpert.expertType}`, value);
        }
    };

    // API Queries
    const { data: experts, isLoading: expertsLoading } = useGetAIExpertsQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: conversations, refetch: refetchConversations } = useGetUserConversationsQuery(
        {
            jwtToken: user?.jwtToken || '',
        },
        {
            skip: !user?.jwtToken,
        }
    );

    const { data: conversationData, refetch: refetchConversation } = useGetAIConversationQuery(
        {
            jwtToken: user?.jwtToken || '',
            conversationId: currentConversation || '',
        },
        {
            skip: !currentConversation,
        }
    );

    // Note: For brand selection - using admin brands endpoint with high limit to fetch all brands
    const { data: brands } = useGetAdminBrandsQuery(
        {
            jwtToken: user?.jwtToken || '',
            limit: 1000, // High limit to fetch all brands (including mock org brands)
        },
        {
            skip: !user?.jwtToken,
        }
    );

    // API Mutations
    const [createConversation] = useCreateAIConversationMutation();
    const [deleteConversation] = useDeleteAIConversationMutation();

    // Streaming chat hook
    const { streamMessage, streamingContent, isStreaming, error: streamError } = useStreamingChat();

    // Filter active experts
    const activeExperts = experts?.filter((expert) => expert.isActive) || [];

    // Sort brands alphabetically by name
    const sortedBrands = useMemo(() => {
        if (!brands?.data) return [];
        return [...brands.data].sort((a, b) => a.name.localeCompare(b.name));
    }, [brands?.data]);

    // Sync local messages with conversation data
    useEffect(() => {
        if (conversationData?.messages) {
            setLocalMessages(conversationData.messages);
            setIsTyping(false);
        }
    }, [conversationData?.messages]);

    // Auto-scroll to bottom when messages change or streaming content updates
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [localMessages, isTyping, isStreaming, streamingContent]);

    const formatMessageTime = (createdAt: string) => {
        return new Date(createdAt).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const handleExpertSelect = async (expert: IAIExpert) => {
        setSelectedExpert(expert);
        
        // Create a new conversation
        try {
            const result = await createConversation({
                jwtToken: user?.jwtToken || '',
                payload: { expertType: expert.expertType }
            }).unwrap();
            
            setCurrentConversation(result.id);
            await refetchConversations();
        } catch (error) {
            message.error('Failed to start conversation');
        }
    };

    const handleConversationSelect = (conversation: IAIConversation) => {
        setCurrentConversation(conversation.id);
        setSelectedExpert(activeExperts.find((e) => e.expertType === conversation.expertType) || null);
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim() || !currentConversation || !selectedExpert) return;

        if (!selectedBrand) {
            message.warning('Please select a brand first to provide brand context');
            return;
        }

        const userMessage = inputValue.trim();
        setInputValue('');

        // Add user message immediately to local state
        const tempUserMessage = {
            id: `temp-${Date.now()}`,
            type: 'user',
            content: userMessage,
            createdAt: new Date().toISOString()
        };

        setLocalMessages(prev => [...prev, tempUserMessage]);
        setIsTyping(true);

        try {
            // Use streaming chat
            const result = await streamMessage({
                expertType: selectedExpert.expertType,
                message: userMessage,
                conversationId: currentConversation,
                brandId: Number(selectedBrand),
                jwtToken: user?.jwtToken || '',
                responseStyle,
            });

            // After streaming completes, refetch to get the persisted messages
            if (result) {
                await refetchConversation();
            }
        } catch (error: any) {
            message.error(error.message || 'Failed to send message');
            setInputValue(userMessage); // Restore the message
            // Remove the temporary message on error
            setLocalMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
        } finally {
            setIsTyping(false);
        }
    };

    const handleDeleteConversation = (conversationId: string) => {
        Modal.confirm({
            title: 'Delete Conversation',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure you want to delete this conversation? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await deleteConversation({
                        jwtToken: user?.jwtToken || '',
                        conversationId: conversationId,
                    }).unwrap();

                    if (currentConversation === conversationId) {
                        setCurrentConversation(null);
                        setSelectedExpert(null);
                    }

                    await refetchConversations();
                    message.success('Conversation deleted');
                } catch (error) {
                    message.error('Failed to delete conversation');
                }
            },
        });
    };


    if (expertsLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', height: 'calc(100vh - 112px)' }}>
            <style dangerouslySetInnerHTML={{ __html: typingAnimationStyle }} />
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Space>
                    <Link to="/admin/ai-experts">
                        <Button icon={<ArrowLeftOutlined />}>
                            Back to AI Experts
                        </Button>
                    </Link>
                    <Title level={2} style={{ margin: 0 }}>
                        AI Expert Chat - Admin Testing
                    </Title>
                </Space>
            </Space>

            <Alert
                message="Admin Testing Mode"
                description="As an admin, you have access to all AI experts without subscription restrictions. Select a brand to include brand equity context in expert responses."
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <Row gutter={24} style={{ height: 'calc(100% - 180px)' }}>
                {/* Left Sidebar - Expert Selection & Conversations */}
                <Col xs={24} md={8} lg={6}>
                    <Card
                        title="AI Experts"
                        size="small"
                        style={{ marginBottom: 16, height: 'auto' }}
                    >
                        {activeExperts.length === 0 ? (
                            <Empty description="No active experts available" />
                        ) : (
                            <List
                                size="small"
                                dataSource={activeExperts}
                                renderItem={(expert) => (
                                    <List.Item
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor:
                                                selectedExpert?.id === expert.id
                                                    ? '#f0f0f0'
                                                    : 'transparent',
                                            padding: '8px',
                                            borderRadius: 4,
                                        }}
                                        onClick={() => handleExpertSelect(expert)}
                                    >
                                        <Space>
                                            <Avatar
                                                style={{
                                                    backgroundColor:
                                                        colorMap[expert.color] || '#F59E0B',
                                                }}
                                                icon={iconMap[expert.icon] || <RobotOutlined />}
                                            />
                                            <div>
                                                <Text strong>{expert.name}</Text>
                                                <br />
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {expert.specialty}
                                                </Text>
                                            </div>
                                        </Space>
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>

                    {/* Brand Selection */}
                    <Card
                        title="Brand Context"
                        size="small"
                        style={{ marginBottom: 16 }}
                        extra={
                            <Tooltip title="Select a brand to include brand equity data in expert responses">
                                <InfoCircleOutlined />
                            </Tooltip>
                        }
                    >
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Select a brand"
                            value={selectedBrand}
                            onChange={setSelectedBrand}
                            allowClear
                            showSearch
                            filterOption={(input, option) => {
                                if (!option?.children) return false;
                                return String(option.children).toLowerCase().includes(input.toLowerCase());
                            }}
                        >
                            {sortedBrands.map((brand) => (
                                <Select.Option key={brand.id} value={brand.id}>
                                    {brand.name}
                                </Select.Option>
                            ))}
                        </Select>
                        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                            {selectedBrand 
                                ? 'Expert responses will include brand equity context' 
                                : 'Select a brand to include brand-specific context'}
                        </Text>
                    </Card>

                    {/* Conversation History */}
                    <Card
                        title="Conversations"
                        size="small"
                        style={{ height: 'calc(100% - 320px)', overflow: 'hidden' }}
                        styles={{ body: {height: 'calc(100% - 40px)', overflow: 'auto'} }}
                    >
                        {conversations && conversations.length > 0 ? (
                            <List
                                size="small"
                                dataSource={conversations}
                                renderItem={(conversation) => {
                                    const expert = activeExperts.find(
                                        (e) => e.expertType === conversation.expertType
                                    );
                                    return (
                                        <List.Item
                                            style={{
                                                cursor: 'pointer',
                                                backgroundColor:
                                                    currentConversation === conversation.id
                                                        ? '#f0f0f0'
                                                        : 'transparent',
                                                padding: '8px',
                                                borderRadius: 4,
                                            }}
                                            onClick={() => handleConversationSelect(conversation)}
                                            actions={[
                                                <Tooltip title="Delete conversation">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteConversation(conversation.id);
                                                        }}
                                                    />
                                                </Tooltip>,
                                            ]}
                                        >
                                            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                                                <Text strong>{expert?.name || 'Unknown Expert'}</Text>
                                                <Text style={{ fontSize: 13 }} ellipsis={{ tooltip: conversation.title }}>
                                                    {conversation.title || 'New Conversation'}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {(() => {
                                                        const dateToUse = conversation.lastMessageAt || conversation.createdAt;
                                                        return dateToUse && !isNaN(new Date(dateToUse).getTime())
                                                            ? `${new Date(dateToUse).toLocaleDateString()} ${new Date(dateToUse).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                                            : 'Recent';
                                                    })()}
                                                </Text>
                                            </Space>
                                        </List.Item>
                                    );
                                }}
                            />
                        ) : (
                            <Empty description="No conversations yet" />
                        )}
                    </Card>
                </Col>

                {/* Main Chat Area */}
                <Col xs={24} md={16} lg={18}>
                    <Card
                        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                        styles={{ body: {flex: 1, display: 'flex', flexDirection: 'column', padding: 0} }}
                    >
                        {selectedExpert && currentConversation ? (
                            <>
                                {/* Chat Header */}
                                <div
                                    style={{
                                        padding: '16px 24px',
                                        borderBottom: '1px solid #f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Space>
                                        <Avatar
                                            size="large"
                                            style={{
                                                backgroundColor:
                                                    colorMap[selectedExpert.color] || '#F59E0B',
                                            }}
                                            icon={iconMap[selectedExpert.icon] || <RobotOutlined />}
                                        />
                                        <div>
                                            <Title level={4} style={{ margin: 0 }}>
                                                {selectedExpert.name}
                                            </Title>
                                            <Text type="secondary">{selectedExpert.specialty}</Text>
                                        </div>
                                    </Space>
                                    <Tag color="green">Admin Access</Tag>
                                </div>

                                {/* Messages Area */}
                                <div
                                    style={{
                                        flex: 1,
                                        overflow: 'auto',
                                        padding: '24px',
                                        backgroundColor: '#fafafa',
                                    }}
                                >
                                    {!currentConversation ? (
                                        <div style={{ 
                                            height: '100%', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            textAlign: 'center'
                                        }}>
                                            <Empty
                                                image={<RobotOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                                                description={
                                                    <div>
                                                        <Title level={4} style={{ color: '#999' }}>
                                                            Start a conversation with {selectedExpert.name}
                                                        </Title>
                                                        <Text type="secondary">
                                                            Ask questions about {selectedExpert.specialty.toLowerCase()} or any brand-related topics
                                                        </Text>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <div style={{ width: '100%' }}>
                                            {localMessages.map((message: any) => {
                                                // API returns 'type' instead of 'messageType'
                                                const isUserMessage = message.type === 'user';
                                                return (
                                                    <div key={message.id} style={{ 
                                                        padding: '8px 0',
                                                        width: '100%'
                                                    }}>
                                                        <div
                                                            style={{
                                                                width: '100%',
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                justifyContent: isUserMessage ? 'flex-end' : 'flex-start',
                                                                alignItems: 'flex-end',
                                                                gap: 8,
                                                                marginLeft: isUserMessage ? 'auto' : '0',
                                                                marginRight: isUserMessage ? '0' : 'auto'
                                                            }}
                                                        >
                                                            {!isUserMessage && (
                                                                <Avatar
                                                                    size={32}
                                                                    style={{ 
                                                                        backgroundColor: colorMap[selectedExpert.color] || '#F59E0B',
                                                                        flexShrink: 0
                                                                    }}
                                                                    icon={iconMap[selectedExpert.icon] || <RobotOutlined />}
                                                                />
                                                            )}
                                                            <div
                                                                style={{
                                                                    maxWidth: '70%',
                                                                    padding: '12px 16px',
                                                                    borderRadius: isUserMessage 
                                                                        ? '12px 12px 4px 12px' 
                                                                        : '12px 12px 12px 4px',
                                                                    backgroundColor: isUserMessage
                                                                        ? '#F59E0B'
                                                                        : '#f5f5f5',
                                                                    color: isUserMessage ? '#fff' : '#000',
                                                                    position: 'relative'
                                                                }}
                                                            >
                                                                <div style={{ marginBottom: 4, whiteSpace: 'pre-wrap' }}>
                                                                    {isUserMessage ? (
                                                                        message.content
                                                                    ) : (
                                                                        <ReactMarkdown
                                                                            components={{
                                                                                h1: ({ children }) => <h1 style={{ fontSize: '1.2em', fontWeight: 'bold', margin: '8px 0 4px 0' }}>{children}</h1>,
                                                                                h2: ({ children }) => <h2 style={{ fontSize: '1.1em', fontWeight: 'bold', margin: '6px 0 3px 0' }}>{children}</h2>,
                                                                                h3: ({ children }) => <h3 style={{ fontSize: '1.05em', fontWeight: 'bold', margin: '4px 0 2px 0' }}>{children}</h3>,
                                                                                p: ({ children }) => <p style={{ margin: '4px 0' }}>{children}</p>,
                                                                                ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ul>,
                                                                                ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ol>,
                                                                                li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
                                                                                strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
                                                                                em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                                                code: ({ children }) => <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace' }}>{children}</code>,
                                                                                blockquote: ({ children }) => <blockquote style={{ borderLeft: '3px solid #ccc', paddingLeft: '12px', margin: '4px 0', fontStyle: 'italic' }}>{children}</blockquote>
                                                                            }}
                                                                        >
                                                                            {message.content}
                                                                        </ReactMarkdown>
                                                                    )}
                                                                </div>
                                                                <div style={{ 
                                                                    fontSize: 11, 
                                                                    opacity: 0.7,
                                                                    textAlign: 'right'
                                                                }}>
                                                                    {formatMessageTime(message.createdAt)}
                                                                </div>
                                                            </div>
                                                            {isUserMessage && (
                                                                <Avatar
                                                                    size={32}
                                                                    style={{
                                                                        backgroundColor: '#F59E0B',
                                                                        flexShrink: 0
                                                                    }}
                                                                    icon={<UserOutlined />}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {(isTyping || isStreaming) && (
                                                <div style={{ padding: '8px 0', width: '100%' }}>
                                                    <div style={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'flex-end',
                                                        gap: 8
                                                    }}>
                                                        <Avatar
                                                            size={32}
                                                            style={{
                                                                backgroundColor: colorMap[selectedExpert.color] || '#F59E0B',
                                                                flexShrink: 0
                                                            }}
                                                            icon={iconMap[selectedExpert.icon] || <RobotOutlined />}
                                                        />
                                                        <div style={{
                                                            maxWidth: '70%',
                                                            padding: '12px 16px',
                                                            borderRadius: '12px 12px 12px 4px',
                                                            backgroundColor: '#f5f5f5',
                                                        }}>
                                                            {streamingContent ? (
                                                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                                                    <ReactMarkdown
                                                                        components={{
                                                                            h1: ({ children }) => <h1 style={{ fontSize: '1.2em', fontWeight: 'bold', margin: '8px 0 4px 0' }}>{children}</h1>,
                                                                            h2: ({ children }) => <h2 style={{ fontSize: '1.1em', fontWeight: 'bold', margin: '6px 0 3px 0' }}>{children}</h2>,
                                                                            h3: ({ children }) => <h3 style={{ fontSize: '1.05em', fontWeight: 'bold', margin: '4px 0 2px 0' }}>{children}</h3>,
                                                                            p: ({ children }) => <p style={{ margin: '4px 0' }}>{children}</p>,
                                                                            ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ul>,
                                                                            ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ol>,
                                                                            li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
                                                                            strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
                                                                            em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                                            code: ({ children }) => <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace' }}>{children}</code>,
                                                                            blockquote: ({ children }) => <blockquote style={{ borderLeft: '3px solid #ccc', paddingLeft: '12px', margin: '4px 0', fontStyle: 'italic' }}>{children}</blockquote>
                                                                        }}
                                                                    >
                                                                        {streamingContent}
                                                                    </ReactMarkdown>
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        width: 8,
                                                                        height: 16,
                                                                        backgroundColor: '#999',
                                                                        marginLeft: 2,
                                                                        animation: 'typing 1s infinite ease-in-out'
                                                                    }} />
                                                                </div>
                                                            ) : (
                                                                <div style={{
                                                                    display: 'flex',
                                                                    gap: 4,
                                                                    alignItems: 'center'
                                                                }}>
                                                                    <div style={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: '#999',
                                                                        animation: 'typing 1.4s infinite ease-in-out'
                                                                    }} />
                                                                    <div style={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: '#999',
                                                                        animation: 'typing 1.4s infinite ease-in-out',
                                                                        animationDelay: '0.2s'
                                                                    }} />
                                                                    <div style={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: '#999',
                                                                        animation: 'typing 1.4s infinite ease-in-out',
                                                                        animationDelay: '0.4s'
                                                                    }} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Input Area */}
                                <div
                                    style={{
                                        padding: '16px 24px',
                                        borderTop: '1px solid #f0f0f0',
                                    }}
                                >
                                    <Space.Compact style={{ width: '100%' }}>
                                        <TextArea
                                            value={inputValue}
                                            onChange={(e) => setInputValue(e.target.value)}
                                            onPressEnter={(e) => {
                                                if (!e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendMessage();
                                                }
                                            }}
                                            placeholder={`Ask ${selectedExpert.name} about ${selectedExpert.specialty.toLowerCase()}...`}
                                            autoSize={{ minRows: 1, maxRows: 4 }}
                                            disabled={isTyping || isStreaming}
                                        />
                                        <Button
                                            type="primary"
                                            icon={<SendOutlined />}
                                            onClick={handleSendMessage}
                                            disabled={!inputValue.trim() || isTyping || isStreaming}
                                        >
                                            Send
                                        </Button>
                                    </Space.Compact>
                                    <div style={{ marginTop: 8 }}>
                                        <Radio.Group
                                            size="small"
                                            value={responseStyle}
                                            onChange={(e) => handleResponseStyleChange(e.target.value)}
                                            optionType="button"
                                            buttonStyle="solid"
                                        >
                                            <Radio.Button value="brief"><MenuFoldOutlined style={{ marginRight: 4 }} />Brief</Radio.Button>
                                            <Radio.Button value="detailed"><MenuUnfoldOutlined style={{ marginRight: 4 }} />Detailed</Radio.Button>
                                        </Radio.Group>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div
                                style={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Empty
                                    description={
                                        <Space direction="vertical" align="center">
                                            <Text>Select an AI expert to start testing</Text>
                                            <Text type="secondary">
                                                You can interact with any expert without subscription restrictions
                                            </Text>
                                        </Space>
                                    }
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};